import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Info, X, ListChecks, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Reusable module-level Help button.
 * Renders a small ⓘ icon next to a page title.
 * On click, fetches /api/help-content?module={moduleKey} and shows a side panel.
 *
 * Props:
 *  - moduleKey: string (required, e.g. "lead", "deal", "quotation")
 *  - defaultTitle: optional fallback title if no help content found
 */
const HelpButton = ({ moduleKey, defaultTitle = "Help" }) => {
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    axios
      .get(`${API}/help-content?module=${encodeURIComponent(moduleKey)}&field_key=`)
      .then((r) => {
        // Module-level entry has empty field_key
        const moduleEntry = (r.data || []).find((x) => !x.field_key);
        setEntry(moduleEntry || null);
      })
      .catch(() => setEntry(null))
      .finally(() => setLoading(false));
  }, [open, moduleKey]);

  return (
    <>
      <button
        data-testid={`help-btn-${moduleKey}`}
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-7 h-7 ml-2 rounded-full text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
        title="Module help"
      >
        <Info size={18} weight="bold" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] flex" data-testid={`help-panel-${moduleKey}`}>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
          <div className="w-full max-w-md bg-white border-l border-slate-200 shadow-2xl overflow-y-auto">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <Info size={18} weight="bold" className="text-blue-700" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-blue-700 font-bold">Module Help</div>
                  <h3 className="font-semibold text-slate-900" style={{fontFamily: 'Outfit, sans-serif'}}>
                    {entry?.title || defaultTitle}
                  </h3>
                </div>
              </div>
              <button data-testid="help-panel-close" onClick={() => setOpen(false)} className="p-2 hover:bg-slate-100"><X size={16} /></button>
            </div>

            <div className="p-5 space-y-5 text-sm">
              {loading ? (
                <div className="text-slate-500">Loading…</div>
              ) : !entry ? (
                <div className="text-slate-500 text-center py-8">
                  <Info size={24} className="mx-auto mb-2 opacity-40" />
                  Help content coming soon for this module.
                </div>
              ) : (
                <>
                  {entry.purpose && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Purpose</div>
                      <p className="text-slate-800 leading-relaxed">{entry.purpose}</p>
                    </div>
                  )}
                  {entry.who_can_use && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Who Can Use</div>
                      <p className="text-slate-800 leading-relaxed">{entry.who_can_use}</p>
                    </div>
                  )}
                  {entry.steps && entry.steps.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1">
                        <ListChecks size={12} weight="bold" /> Steps
                      </div>
                      <ol className="space-y-1.5 list-decimal list-inside text-slate-800">
                        {entry.steps.map((s, i) => (
                          <li key={i} className="leading-relaxed">{s}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {entry.important && (
                    <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-0.5 flex items-center gap-1">
                        <CheckCircle size={12} weight="bold" /> Important
                      </div>
                      <p className="text-amber-900 leading-relaxed">{entry.important}</p>
                    </div>
                  )}
                  {entry.common_mistakes && (
                    <div className="bg-rose-50 border-l-4 border-rose-500 px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-rose-800 mb-0.5 flex items-center gap-1">
                        <Warning size={12} weight="bold" /> Common Mistakes
                      </div>
                      <p className="text-rose-900 leading-relaxed">{entry.common_mistakes}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpButton;
